// This file contains a function responsible for managing the form structure within the dashboard builder.
// It handles the configuration and behavior of views, sections, widgets, and fields


import shortUuid from 'short-uuid';
import { findIconSource, findTriggerEvent, formatFetchedCustomIcons, generateIconFileName, isValidSimpleIdentifier, validateNonAsciiChars } from "../../utils/commonUtils";
import { typeItems, urlTypes, actionRequiredTypes, defaultValSchema, colorschema, colorSupportedTypes, axisSchema, stepSchema, controlTypes, defaultDimensions, displayWithDimension, defaultFieldSchema, defaultValueTypes, dimensionTypes, generateUpdatedMessageFields, htmlFormSchema, hyperlinkSchema, hyperlinkSupportedTypes, iconSchema, iconValueTypes, lineChartTypesSchema, lineChartValueTypes, mapTypesSchema, mapValueTypes, maxValueTypes, minMaxSchema, minValueTypes, orientationSchema, orientationTypes, otherDisplayTypes, statusTypeSchema, statusValueTypes, uiTypeChildren, unitSchema, unitValueTypes, urlItems } from '../../config/fields';
import { findChildren } from './simulations';
import shortUUID from 'short-uuid';
import { prettyPrint } from '@base2/pretty-print-object';
import { validateQuerayParametersInHyperlink } from '../../config/validations';


// ########################### VIEWS #####################################


export const generateViewsCode = (sampleData) => {
  sampleData = sampleData || {};
  const formattedCode = `async function init() {

        /** 
        Available global variables
        var VERSION
        var APPID
        var RSLICE
        var SSLICE
        */
            
        let data = ${prettyPrint(sampleData, {
    indent: ' ',
    singleQuotes: false
  })};
            
        // Code to prepare data
            
        return data;
    }`;
  return formattedCode;
}



export const viewFormSchema = {
  type: 'object',
  required: ['name', 'title'],
  options: {
    layout: 'grid',
    disable_properties: 'false'
  },
  properties: {
    name: {
      title: 'Name',
      type: 'string',
      options: {
        layout: 'grid',
        grid_columns: 12
      },
    },
    title: {
      title: 'Title',
      type: 'string',
      options: {
        layout: 'grid',
        grid_columns: 12
      },
    },
    desc: {
      title: 'Description',
      type: 'string',
      format: 'textarea',
      options: {
        layout: 'grid',
        grid_columns: 12
      },
    },
    instance_prompt: {
      title: 'Instance Prompt',
      type: 'string',
      description: 'Prompt shown to users : "Enter view instance"',
      options: {
          layout: 'grid',
          grid_columns: 12
      },  
    },
    visible: {
      title: 'Visibility',
      type: "string",
      enumSource: [{
        source: [{ title: "True", value: "true" }, { title: "False", value: "false" }],
        title: "{{item.title}}",
        value: "{{item.value}}"
      }],
      options: {
        layout: 'grid',
        grid_columns: 12
      }
    }
  },
}

export const viewFormSectionSchema = {
  "sections": {
    "type": "array",
    "title": "Sections",
    "format": "table",
    "items": {
      "type": "object",
      "title": "Sections",
      "properties": {
        "name": {
          "type": "string",
          "title": "Name",
          "options": {
            "layout": "grid",
            "grid_columns": 12
          }
        }
      },
      "format": "grid",
      "options": {
        "layout": "grid",
        "disable_properties": "false"
      }
    }
  }


}


export const getViewSchemaData = (selectedView) => {
  if (selectedView) {
    return {
      name: selectedView.name || '',
      title: selectedView.title || '',
      desc: selectedView.desc || '',
      visible: String(selectedView.visible) || "true",
      instance_prompt: selectedView?.instance_prompt || ""
    }
  } else {
    return {
      name: "",
      title: "",
      desc: "",
      visible: "true",
      instance_prompt: "",
      sections: [{
        name: "section1"
      }]
    }
  }
}


export const customiseViewForm = (data) => {

  const viewForm = document.getElementById("view_form");
  if (viewForm) {

    // To add required asterisk
    const fieldsRequired = viewForm.querySelectorAll('label.required');
    if (fieldsRequired.length > 0) {
      fieldsRequired.forEach(labelElement => {
        const containsSpan = labelElement.querySelector('span') !== null;
        if (!containsSpan) {
          const asterisk = document.createElement('span');
          asterisk.textContent = ' *';
          asterisk.style = 'color: red;';
          labelElement.appendChild(asterisk);
        }
      });
    }


    const sectionsContainer = document.querySelector(`[data-schemapath="view_form.sections"]`);
    if (sectionsContainer) {

      const addBtn = sectionsContainer.querySelector("button.btn.btn-secondary.btn-sm.json-editor-btn-add.json-editor-btntype-add");

      if (addBtn) {
        addBtn.textContent = 'Add';
        addBtn.style.color = '#3f4e5c';
        addBtn.style.backgroundColor = '#fff';
        addBtn.style.border = '1px solid';
        addBtn.style.borderRadius = '5px';
      }
      const sectionTable = sectionsContainer.querySelector("table.table.table-sm");
      if (sectionTable) {
        if (data.sections.length == 0) {
          sectionTable.style.display = 'none';
        }

        // To make heading grid to center
        const gridHead = sectionTable.querySelector("thead tr th:nth-child(2)");
        if (gridHead) {
          gridHead.style.textAlign = 'center';
        }


        const rows = sectionTable.querySelectorAll('tbody tr');
        if (rows.length > 0) {
          rows.forEach((row, index) => {
            //To Hide the span with class 'h3 card-title level-4 je-object__title'
            const titleSpan = row.querySelector('td:nth-child(2) .h3.card-title.level-4.je-object__title');
            if (titleSpan) {
              titleSpan.style.display = 'none';
            }

            const controlSpan = row.querySelector('td:nth-child(2) .btn-group.je-object__controls');
            if (controlSpan) {
              controlSpan.style.display = 'none';
            }

            //To Hide all labels in the second column
            const labels = row.querySelectorAll('td:nth-child(2) label');
            labels.forEach(label => label.style.display = 'none');

            // Hide the properties button
            const propertiesButton = row.querySelector('td:nth-child(2) .json-editor-btn-edit_properties');
            if (propertiesButton) {
              propertiesButton.style.display = 'none';
            }

            // To customise remove button
            const deleteBtn = row.querySelector('td:nth-child(3) .btn.btn-secondary.btn-sm.json-editor-btn-delete.delete.json-editor-btntype-delete');
            if (deleteBtn) {
              if (rows.length == 1) {
                deleteBtn.style.display = 'none';
              } else {
                deleteBtn.textContent = 'Remove';
                deleteBtn.style.marginTop = '5px';
                deleteBtn.style.color = '#3f4e5c';
                deleteBtn.style.backgroundColor = '#fff';
                deleteBtn.style.border = '1px solid';
                deleteBtn.style.borderRadius = '5px';
              }
            }
          });
        }

      }
    }
  }
}




export const generateViewSchema = ({ name, desc, title, visible, sections, icon }) => {

  const viewSchemaObj = {
    name,
    title,
    desc,
    view_schema: [],
    code: null,
    visible
  }
  if (icon) {
    viewSchemaObj.icon = generateIconFileName(icon);
    viewSchemaObj.iconSource = findIconSource(icon);
  }

  if (sections.length > 0) {
    sections.forEach(section => {
      const sectionSchema = generateSectionSchema({ name: section.name });
      viewSchemaObj.view_schema.push(sectionSchema);
    });
  }
  return viewSchemaObj;
}



// ########################### Section #####################################


export const generateSectionSchema = (sectionData) => {
  return {
    ...sectionData,
    visible: sectionData.visible === "false" || sectionData.visible === false ? false : true,
    infotype: 'info',
    type: 'struct',
    id: shortUuid.generate(),
    category: 'section',
    dimensions: "12x1",
    mapping: {},
    children: []
  }
}


export const viewFormValidation = (schema, value, path) => {

  let errors = [];
  if (path === "view_form.name" && value === "") {
    errors.push({
      path,
      property: 'format',
      message: "Name is required"
    });
  }

  if (path === "view_form.name" && isValidSimpleIdentifier(value) === false) {
    errors.push({
      path,
      property: 'format',
      message: "Name can only contain letters, numbers, and underscores"
    });
  }


  if (path === "view_form.title" && value === "") {
    errors.push({
      path,
      property: 'format',
      message: "Title is required"
    });
  }

  if (path === "view_form.sections.0.name" && value === "") {
    errors.push({
      path,
      property: 'format',
      message: "Atleast one section is required"
    });
  }

  const regex = /^view_form\.sections\.\d+\.name$/;
  const isMatch = regex.test(path);

  if (isMatch && (isValidSimpleIdentifier(value) === false || value === "")) {
    errors.push({
      path,
      property: 'format',
      message: "Section name cannot be empty and may only include letters, numbers, and underscores"
    });
  }


  if (path === "view_form.desc" && validateNonAsciiChars(value) !== true) {
    errors.push({
      path,
      property: 'format',
      message: validateNonAsciiChars(value)
    })
  }

  return errors;
}



export const sectionFormSchema = {
  type: 'object',
  required: ['name'],
  options: {
    layout: 'grid',
    disable_properties: 'false'
  },
  properties: {
    name: {
      title: 'Name',
      type: 'string',
      options: {
        layout: 'grid',
        grid_columns: 12
      },
    },
    desc: {
      title: 'Description',
      type: 'string',
      format: 'textarea',
      options: {
        layout: 'grid',
        grid_columns: 12
      },
    }
  }
}

export const dimensionSchema = {
  width: {
    title: "Width",
    type: "string",
    enum: ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
    format: "grid",
    options: {
      layout: "grid",
      grid_columns: 2
    }
  },
  height: {
    title: "Height",
    type: "string",
    enum: ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
    format: "grid",
    options: {
      layout: "grid",
      grid_columns: 2
    }
  }
}


export const getSectionSchemaData = (selectedSection) => {
  return {
    name: selectedSection?.name || '',
    desc: selectedSection?.desc || '',
  }
}


export const sectionFormValidation = ({ schema, value, path, sections, selectedSection }) => {
  let errors = [];

  if (path == "section_form.name" && value == "") {
    errors.push({
      path,
      property: 'format',
      message: "Name is required"
    });
  }

  if (path == "section_form.name" && isValidSimpleIdentifier(value) === false) {
    errors.push({
      path,
      property: 'format',
      message: "Name can only contain letters, numbers, and underscores"
    });
  }

  if (path == "section_form.name" && sections?.includes(value) && selectedSection?.name != value) {
    errors.push({
      path,
      property: 'format',
      message: "Section already exists"
    });
  }

  if (path == "section_form.desc" && validateNonAsciiChars(value) !== true) {
    errors.push({
      path,
      property: 'format',
      message: validateNonAsciiChars(value)
    })
  }

  if (path == "section_form.height" && value === 'select') {
    errors.push({
      path,
      property: 'format',
      message: "Height should be selected"
    })
  }

  if (path == "section_form.width" && value === 'select') {
    errors.push({
      path,
      property: 'format',
      message: "Width should be selected"
    })
  }


  return errors;
}



/**
 * ########################### WIDGETS #####################################
 */

// List of available widget
const widgetList = [
  ...otherDisplayTypes,
  ...urlItems,
  { title: 'Image Map', value: 'image-map', includes: ["icon", "unit"] },
  { title: 'String', value: 'string' },
  { title: 'Chat Box', value: 'chat' }
].sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1));

const attachProperties = (schema, property) => {
  schema.properties = {
    ...schema.properties,
    ...property
  }

}

const visibilitySchema = {
  "visible": {
    title: 'Visibility',
    type: "string",
    enumSource: [{
      source: [{ title: "True", value: "true" }, { title: "False", value: "false" }],
      title: "{{item.title}}",
      value: "{{item.value}}"
    }]
  }
}

export const generateWidgetSchema = ({ transactionList, clusterletList, viewsList, selectedChild }) => {
  const typesSchema = [
    { title: 'Select', value: 'select' },
    ...widgetList
  ].map((item) => {
    let widgetSchema = {
      title: item.title,
      type: 'object',
      default: 'control-button',
      properties: {
        [item.value]: {
          default: item.value,
          type: 'string',
          options: {
            hidden: true
          }
        },
      },
      additionalProperties: false,
    };

    if (iconValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, iconSchema)
    }

    if (unitValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, unitSchema)
    }

    if (defaultValueTypes.includes(item.value)) {
      let defaultSchema = defaultFieldSchema;
      if (item.value === 'counter') defaultSchema.default.default = 0;
      attachProperties(widgetSchema, defaultSchema)
    }

    if (minValueTypes.includes(item.value) || maxValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, minMaxSchema)
    }

    // if (dimensionTypes.includes(item.value)) {
    //     attachProperties(widgetSchema, widthHeightSchema)
    // }

    if (orientationTypes.includes(item.value)) {
      attachProperties(widgetSchema, orientationSchema)
    }

    if (mapValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, mapTypesSchema)
    }

    if (lineChartValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, lineChartTypesSchema)
    }

    if (statusValueTypes.includes(item.value)) {
      attachProperties(widgetSchema, statusTypeSchema)
    }

    if (item.value === 'html_form') {
      attachProperties(widgetSchema, htmlFormSchema(transactionList))
    }

    if (hyperlinkSupportedTypes.includes(item.value)) {
      const hyperlinkSchemaObj = hyperlinkSchema(clusterletList, viewsList);
      attachProperties(widgetSchema, hyperlinkSchemaObj)
    }

    if (colorSupportedTypes?.includes(item.value)) {
      attachProperties(widgetSchema, colorschema)
    }

    return widgetSchema;
  });

  let widgetSchema = {
    type: 'object',
    required: ['name', 'type', 'desc'],
    options: {
      layout: 'grid',
      disable_properties: 'false',
    },
    properties: {
      name: {
        title: 'Name',
        type: 'string',
        required: true,
        options: {
          layout: 'grid',
          grid_columns: 12,
        },
      },
      desc: {
        title: 'Description',
        type: 'string',
        format: 'textarea',
        options: {
          input_height: '80px',
          layout: 'grid',
          grid_columns: 12,
        },
      },
      type: {
        title: 'Type',
        oneOf: [...typesSchema],
        options: {
          input_width: '200px',
          disable_properties: true,
          layout: 'grid',
          grid_columns: 12,
        },
      },
    },
  };

  

  if (selectedChild?.viewCopy === true) {
    widgetSchema.properties = { ...widgetSchema?.properties, ...visibilitySchema };
  }

  return widgetSchema;
}

export const organizeWidgetData = ({ data, extendedFieldsData, eventFields, action, parentID, asset, url, areas, selectedWidgetData: selectedChild, oldType }) => {
  try {
  let { name, desc, type, visible } = data

  const selectedType = Object.keys(type)[0];

  const widgetSchema = {
    name: name,
    type: selectedType,
    desc: desc,
    visible: visible === "false" ? false : true,
    infotype: 'info',
    category: 'ui',
  };

  let widgetChildren = uiTypeChildren()
  widgetSchema.children = selectedChild ? selectedChild.children : widgetChildren[selectedType] || [];

  if (selectedType === 'table') {
    widgetSchema.collection = true;
    widgetSchema.tableactions = [];
  }

  if (selectedType === 'model-3d') {
    widgetSchema.position = '+0+0';
    widgetSchema.infotype = 'numeric';
  }

  if (selectedType === 'counter') {
    widgetSchema.infotype = 'numeric';
    if (type?.icon && type.icon !== 'select') {
      if (type?.icon === "custom_icon") {
        let icon = data?.customicon;
        widgetSchema.customicon = generateIconFileName(icon);
        widgetSchema.iconSource = findIconSource(icon);
      } else {
        widgetSchema.icon = type?.icon;
        widgetSchema.iconSource = 'built-in'
        delete widgetSchema.customicon;
      }
    }
  }
  if (selectedType === 'status') {
    if (type.statusType !== '' && type.statusType !== undefined && type.statusType !== 'select') widgetSchema.statusType = type.statusType;
  }

  if (action === "create") {
    widgetSchema.dimensions = defaultDimensions[selectedType]
    if (selectedType === "control-slider" && type.orientation === "vertical") {
      widgetSchema.dimensions = "2x3"
    }
  } else {
    widgetSchema.dimensions = selectedChild.dimensions;
  }

  if (type.minval !== undefined && type.maxval !== '') widgetSchema.maxval = type.maxval;
  if (type.minval !== undefined && type.minval !== '') widgetSchema.minval = type.minval;
  if (type.default !== undefined) {
    try {
      widgetSchema.default = JSON.parse(type.default);
    } catch (e) {
      // Not a valid JSON 
      widgetSchema.default = type.default;
    }
  };
  if (type.unit !== '' && type.unit) widgetSchema.unit = type.unit;
  if (type.orientation && type.orientation !== 'select') {
    widgetSchema.orientation = type.orientation;
  }
  if (type.mapType && type.mapType !== 'select') {
    widgetSchema.mapType = type.mapType;
  }
  if (type.linetype && type.linetype !== 'select') {
    widgetSchema.linetype = type.linetype;
  }

  if (hyperlinkSupportedTypes.includes(selectedType)) {
    if (type.hyperlink.enable) {
      const urlType = type.hyperlink.urlType
      const queryParams = urlType?.queryParameters?.filter(item => item?.Key !== "" && item?.Value !== "");
      if (urlType.viewname !== undefined) {
        widgetSchema.viewname = urlType.viewname
        widgetSchema.instance = urlType.instance
      }
      if (urlType.clusterlettype !== undefined) {
        widgetSchema.clusterlettype = urlType.clusterlettype
        widgetSchema.instance = urlType.instance
      }
      if((urlType.viewname !== undefined || urlType.clusterlettype !== undefined) && queryParams?.length > 0){
        widgetSchema.queryparams = queryParams;
      }else{
        delete widgetSchema.queryparams;
      }

      if (urlType.url !== undefined) {
        widgetSchema.url = urlType.url
      }

      
    }
  }

  if (selectedType === "html_form") {
    let extended_fields = extendedFieldsData?.length > 0 ? extendedFieldsData : eventFields;
    extended_fields = extended_fields?.map(field => {
      const { defaultConstraint, ...rest } = field; 
      return rest;
    });

    if (type?.gettertransaction && type?.gettertransaction !== 'select') widgetSchema.gettertransaction = type?.gettertransaction;
    if (type?.getterinstance) widgetSchema.getterinstance = type?.getterinstance;
    if (type?.settertransaction !== undefined && type?.settertransaction !== 'select') widgetSchema.settertransaction = type?.settertransaction;
    if (type?.setterinstance) widgetSchema.setterinstance = type?.setterinstance;
    if (type?.buttontext) widgetSchema.buttontext = type?.buttontext;
    widgetSchema.setter = {}; // added to show form widget in IDE. It will be removed before save in to DB at backend
    if (extended_fields.length > 0) {
      widgetSchema.extendedfields = extended_fields;
      widgetSchema.setter.fields = updateExtendedFieldsForDashboardPreview(JSON.parse(JSON.stringify(extended_fields)));
      widgetSchema.setter.instance = type?.setterinstance;
      widgetSchema.setter.clusterlettype = "";
      widgetSchema.setter.event = "";
    }
  }

  if (colorSupportedTypes?.includes(selectedType)) {

    if (type?.backgroundColor != undefined) {
      widgetSchema.bgcolor = type?.backgroundColor;
    }
    if (type?.textColor != undefined) {
      widgetSchema.textcolor = type?.textColor;
    }
  }


  if (action === 'create') {
    // For adding default values for image map
    if (selectedType === 'image-map') {
      widgetSchema.asset = asset;
      widgetSchema.children.map((child) => {
        if (url && child.name == 'url') {
          child.default = url;
        }
        if (areas && child.name === 'areas') {
          child.default = areas;
        }
      });
    }


    widgetSchema.id = shortUUID.generate();
    widgetSchema.mapping = {};
  }

  const lineBarCharts = ['chart-bar', 'chart-line', 'chart-ratiobar'];

  if (action === 'edit') {
    widgetSchema.id = selectedChild.id;
    widgetSchema.mapping = selectedChild.mapping || {};
    if (selectedChild.positions) widgetSchema.positions = selectedChild.positions;
    if (selectedChild.position) widgetSchema.position = selectedChild.position;
    if (selectedChild?.viewCopy === true) widgetSchema.viewCopy = true;

    let children = [];
    
    if (selectedType === oldType) {
      children = selectedChild.children;
    }
    
    if (selectedType !== oldType && !(lineBarCharts.includes(selectedType) && lineBarCharts.includes(oldType))) {
      children = widgetChildren[selectedType];
    }


    // For adding default values for image map
    if (selectedType === 'image-map') {
      if (asset !== undefined) {
        widgetSchema.asset = asset;
      } else {
        widgetSchema.asset = selectedChild.asset;
      }

      children?.map((child) => {
        if (url && url !== '' && child.name === 'url') {
          child.default = url;
        }
        if (areas && child.name === 'areas') {
          child.default = areas;
        }
        return child;
      });
    }

    if (selectedType === 'table') {
      if (selectedChild.tableactions !== undefined) widgetSchema.tableactions = selectedChild.tableactions;
    }

    if (controlTypes.includes(selectedType) || actionRequiredTypes.includes(selectedType) || selectedType === "chat") {

      if (selectedChild.transaction !== undefined) widgetSchema.transaction = selectedChild.transaction;
      widgetSchema.event = selectedChild.event;
      widgetSchema.clusterlettype = selectedChild.clusterlettype;
      widgetSchema.instance = selectedChild.instance;
      widgetSchema.extendedfields = selectedChild.extendedfields;
    }

    if (selectedChild?.viewCopy === true && selectedChild?.children?.length > 0) {

      widgetSchema.children = selectedChild.children;
    }

    widgetSchema.children = children === undefined ? [] : children.length > 0 ? children : widgetSchema.children;

  }

  return widgetSchema;

  } catch (error) {
    console.error(`Organising  widget data failed. ${error}`);
    return null;
  }
}

export const updateExtendedFieldsForDashboardPreview = (fields) => {
  try {
  
  const updateFields = (children) => {
    return children?.map(field => {
      if (field?.constraint && field?.constraint !== "") {
            field.constraint = generateDropdownValuesForContraint(field.constraint);
      }

      if(field.children && field.children > 0){
        field.children = updateFields(field.children)
      }

      return field;
    })
  }

  return updateFields(fields || [])

  } catch (error) {
    console.error(`Updating extended fields for dashboard preview failed. ${error}`);
    return fields;
  }
}


const generateDropdownValuesForContraint = (constraint) => {
  try {
    if (constraint?.includes('..')) {
      return null;
    } else {
      let formattedValue = "";
      if (typeof constraint == 'string') {
        const parts = constraint.split(',');
        const transformed = parts.map(part => `${part.trim()}:${part.trim()}`);
        formattedValue = transformed.join(',');
      } else {

        formattedValue = constraint.map(item => `${item?.label}:${item?.value}`).join(',');
      }
      return formattedValue;
    }

  } catch (error) {
    console.error(error);
    return "";
  }
}

function validateKey(key) {
  const regex = /^[a-zA-Z0-9_-]+$/;
  return regex.test(key);
}

function validateValue(value) {
  return value.trim().length > 0; // simple check, can extend
}

export const widgetFormValidation = (data) => {
  try {
    
  
  let errors = [];

  if (data.type.select !== undefined) {
    errors.push({
      path: 'ui.type',
      property: 'format',
      message: 'Type is required',
    });
  }
  if (data?.type?.width === 'select') {
    errors.push({
      path: "ui.type.width",
      property: 'format',
      message: "Width should be selected"
    })
  }

  if (data?.type?.height === 'select') {
    errors.push({
      path: "ui.type.height",
      property: 'format',
      message: "Height should be selected"
    })

  }

  if (data.type.btn !== undefined) {
    errors.push({
      path: 'ui.type',
      property: 'format',
      message: 'Type is required',
    });
  }

  if (data?.type?.settertransaction === 'select') {
    errors.push({
      path: "ui.type.settertransaction",
      property: 'format',
      message: "Setter Transaction should be selected"
    })
  }

  if (data.desc && validateNonAsciiChars(data.desc) !== true) {
    errors.push({
      path: 'ui.desc',
      property: 'format',
      message: validateNonAsciiChars(data.desc)
    })
  }

  const selectedType = Object.keys(data.type)[0];
  //  validation for hyperlinks
  if (hyperlinkSupportedTypes.includes(selectedType)) {
    if (data.type.hyperlink.enable) {
      const urlType = data.type.hyperlink.urlType;
      const queryParameters = urlType?.queryParameters || []

      // if url type is not selected
      if (urlType?.urlView === undefined && urlType?.urlClusterlet === undefined && urlType?.urlExternal === undefined) {
        errors.push({
          path: "ui.type.hyperlink.urlType",
          property: 'format',
          message: "Url type is required"
        })
      }

      // validating viewname
      if (urlType?.urlView != undefined) {
        if (urlType?.viewname === 'select') {
          errors.push({
            path: "ui.type.hyperlink.urlType.viewname",
            property: 'format',
            message: "View is required"
          })
        }
      }

      // validating clusterlet type
      if (urlType?.urlClusterlet !== undefined) {
        if (urlType?.clusterlettype === "select") {
          errors.push({
            path: "ui.type.hyperlink.urlType.clusterlettype",
            property: 'format',
            message: "Clusterlet type is required"
          })
        }
      }

      // validating instance field
      if (urlType?.viewname !== undefined || urlType?.clusterlettype !== undefined) {
        if (urlType?.instance === "") {
          errors.push({
            path: "ui.type.hyperlink.urlType.instance",
            property: 'format',
            message: "Instance is required"
          })
        }

      }

      //  validating url field
      if (urlType?.urlExternal !== undefined) {
        if (urlType?.url == "") {
          errors.push({
            path: "ui.type.hyperlink.urlType.url",
            property: 'format',
            message: "Url is required"
          })
        }
      }

      // Query params
      if(queryParameters && queryParameters?.length > 0){
        validateQuerayParametersInHyperlink(queryParameters, errors, 'ui.type.hyperlink.urlType.queryParameters')
      }

    }
  }

  return errors;

  } catch (error) {
    console.error(`Widget form validation failed. ${error}`);
    return [];
  }
}

export const generateWidgetSchemaValues = ({ selectedChild, fields, transactions, clusterlets, events, messages, widgetType, setEventFields, setFieldsHistory, setExtendedFieldsData }) => {
  try {
  let schemaValues;

  if (!selectedChild && widgetType?.value) {
    selectedChild = {
      "type": widgetType.value,
    }
  }
  
  if (selectedChild) {
    const dimensions = selectedChild?.dimensions || defaultDimensions[selectedChild?.type];
    const width = dimensions?.split('x')[0] ? dimensions?.split('x')[0] : '';
    const height = dimensions?.split('x')[1] ? dimensions?.split('x')[1] : '';
    const minVal = selectedChild.minval ? selectedChild.minval : 0;
    const maxVal = selectedChild.maxval ? selectedChild.maxval : 100;
    schemaValues = { ...selectedChild };
    schemaValues.type = { [selectedChild.type]: selectedChild.type };

    if (unitValueTypes.includes(selectedChild.type)) {
      schemaValues.type.unit = selectedChild.unit || ''
    }

    // if (dimensionTypes.includes(selectedChild.type)) {
    //   schemaValues.type.width = width;
    //   schemaValues.type.height = height;
    // }
    if (minValueTypes.includes(selectedChild.type) || maxValueTypes.includes(selectedChild.type)) {
      schemaValues.type.maxval = maxVal;
      schemaValues.type.minval = minVal;
    }
    if (defaultValueTypes.includes(selectedChild.type)) {
      schemaValues.type.default = typeof selectedChild.default === 'object' ? JSON.stringify(selectedChild.default) : (selectedChild.default || selectedChild.default == 0) ? selectedChild.default.toString() : ''
    }
    if (orientationTypes.includes(selectedChild.type)) {
      schemaValues.type.orientation = selectedChild.orientation || 'select'
    }
    if (mapValueTypes.includes(selectedChild.type)) {
      schemaValues.type.mapType = selectedChild.mapType || 'select'
    }
    if (lineChartValueTypes.includes(selectedChild.type)) {
      schemaValues.type.linetype = selectedChild.linetype || 'select'
    }
    if (iconValueTypes.includes(selectedChild.type)) {
      if (selectedChild?.customicon && selectedChild?.customicon != "") {
        schemaValues.type.icon = "custom_icon";
        if (selectedChild?.customicon) schemaValues.customicon = selectedChild?.customicon
        if (["custom", "library"].includes(selectedChild?.iconSource)) {
          schemaValues.customicon = formatFetchedCustomIcons({ iconSource: selectedChild?.iconSource, icon: selectedChild?.customicon })
        }
      } else {
        schemaValues.type.icon = selectedChild.icon || 'select'
      }
    }

    if (colorSupportedTypes?.includes(selectedChild.type)) {
      schemaValues.type.backgroundColor = selectedChild?.bgcolor || "#ffffff";
      schemaValues.type.textColor = selectedChild?.textcolor || "#34495e";
    }



    if (statusValueTypes.includes(selectedChild.type)) {
      schemaValues.type.statusType = selectedChild.statusType || 'select';
    }

    if (selectedChild.viewCopy != undefined && selectedChild.viewCopy === true) {
      schemaValues.visible = selectedChild?.visible != undefined ? selectedChild?.visible : true;
    }

    if (selectedChild.type === 'html_form') {
      schemaValues.type.gettertransaction = selectedChild?.gettertransaction || 'select'
      schemaValues.type.getterinstance = selectedChild?.getterinstance || ""
      schemaValues.type.settertransaction = selectedChild?.settertransaction || 'select'
      schemaValues.type.setterinstance = selectedChild?.setterinstance || ""
      schemaValues.type.buttontext = selectedChild?.buttontext || ""
      if (selectedChild.extendedfields) {
        const transactionData = transactions?.find(txn => txn._id === selectedChild?.settertransaction);
        const triggerringEvent = findTriggerEvent({ transaction: transactionData, events, clusterlets });
        if(triggerringEvent){
          let eventMessage = messages.find(msg => msg._id === triggerringEvent?.message)
          let messageChildren = findChildren(eventMessage, [], fields)
  
          const updatedMessageFields = generateUpdatedMessageFields(selectedChild.extendedfields, messageChildren?.children || [])
          if (setEventFields) {
            setEventFields(updatedMessageFields)
            setExtendedFieldsData(updatedMessageFields);
          }
  
          if (setFieldsHistory) {
            setFieldsHistory({
              [selectedChild.settertransaction]: updatedMessageFields
            })
          }
        }
      }


    }

    if (hyperlinkSupportedTypes.includes(selectedChild.type)) {
      schemaValues.type.hyperlink = { "disable": "disable" }
      if (selectedChild.viewname !== undefined) {
        let urlType = {
          "urlView": "urlView",
          "viewname": selectedChild.viewname,
          "instance": selectedChild.instance || ''
        }
        if(selectedChild?.queryparams && selectedChild?.queryparams?.length > 0){
          urlType.queryParameters = selectedChild?.queryparams
        }else{
          urlType.queryParameters = [{Key: '', Value: ''}]
        }

        schemaValues.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }

        
      }

      if (selectedChild.clusterlettype !== undefined) {
        let urlType = {
          "urlClusterlet": "urlClusterlet",
          "clusterlettype": selectedChild.clusterlettype,
          "instance": selectedChild.instance || ''
        }
        if(selectedChild?.queryparams && selectedChild?.queryparams?.length > 0){
          urlType.queryParameters = selectedChild?.queryparams
        }else{
          urlType.queryParameters = [{Key: '', Value: ''}]
          
        }
        schemaValues.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }
      }

      if (selectedChild?.type === "counter" && selectedChild?.default === "") {
        schemaValues.type.default = "0"
      }

      if (selectedChild?.type?.default) String(selectedChild?.type?.default)

      if (selectedChild.url !== undefined) {
        const urlType = {
          "urlExternal": "urlExternal",
          "url": selectedChild.url || ''
        }
        schemaValues.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }
      }
    }
  }

  return schemaValues;

  } catch (error) {
    console.error(`Generating widget schema failed. ${error}`);
    console.log({selectedChild, fields, transactions, clusterlets, events, messages, widgetType, setEventFields, setFieldsHistory, setExtendedFieldsData });
    return null;
  }
};






// ########################### FIELD #####################################

export const generateFieldSchema = (fieldData) => {
  return {
    visible: fieldData.visible !== "false" && fieldData.visible !== false,
    infotype: 'info',
    id: shortUuid.generate(),
    category: 'field',
    children: [],
    mapping: {},
    ...fieldData,
  }
}


export const getFieldFormSchema = (parentData, clusterletList, viewsList, field) => {
  let uiFieldTypes = parentData?.type === 'table' ? [...typeItems, ...urlItems] : typeItems;

  const fieldTypesSchema = [{ title: "Select", value: 'select' }, ...uiFieldTypes].map((item) => {

    let singleSchema = {
      title: item.title,
      type: "object",
      default: item.value,
      properties: {
        [item.value]: {
          default: item.value,
          type: 'string',
          options: {
            hidden: true
          }
        },

      },
      additionalProperties: false
    };

    singleSchema = { ...singleSchema, properties: { ...singleSchema.properties, ...unitSchema, ...defaultValSchema } };

    if (displayWithDimension.includes(parentData?.type) && parentData?.type !== "chart-geomap") {
      singleSchema = { ...singleSchema, properties: { ...singleSchema.properties, ...axisSchema } };
    }

    if (parentData?.type === 'control-slider') {
      singleSchema = { ...singleSchema, properties: { ...singleSchema.properties, ...minMaxSchema, ...stepSchema } };
    }

    if (parentData?.type === "table" && item.value !== "table-button" && !urlTypes.includes(item.value)) {
      const hyperlinkSchemaObj = hyperlinkSchema(clusterletList, viewsList);
      singleSchema = {
        ...singleSchema,
        properties: { ...singleSchema.properties, ...hyperlinkSchemaObj }
      }
    }

    return singleSchema;

  });

  let schema = {
    type: 'object',
    required: ['name'],
    options: {
      layout: 'grid',
      disable_properties: 'false'
    },
    properties: {
      name: {
        title: 'Name',
        type: 'string',
        readonly: field?.viewCopy,
        options: {
          layout: 'grid',
          grid_columns: 12
        },
      },
      desc: {
        title: 'Description',
        type: 'string',
        format: 'textarea',
        options: {
          layout: 'grid',
          grid_columns: 12
        },
      },
      type: {
        title: 'Type',
        oneOf: [...fieldTypesSchema],
        options: {
          input_width: '200px',
          disable_properties: true,
          layout: 'grid',
          grid_columns: 12
        }
      },
    }
  }

  if (field?.viewCopy === true) {
    schema = {
      ...schema,
      properties: { ...schema.properties, ...visibilitySchema }
    }
  }

  return schema;
}



export const getFieldSchemaData = (selectedField, parentData) => {
  if (selectedField) {
    let schemaData = {
      name: selectedField.name || '',
      desc: selectedField.desc || '',
      type: {
        [selectedField.type]: selectedField.type
      }
    }
    if (displayWithDimension.includes(parentData.type)) {
      schemaData.type.axis = selectedField.axis ?? 'select';
    }

    if (parentData.type === 'control-slider') {
      schemaData.type.step = selectedField.step?.toString() ?? "";
      schemaData.type.maxval = selectedField.maxval ?? 100;
      schemaData.type.minval = selectedField.minval ?? 0;
    }

    // For image map areas
    if (typeof selectedField.default === 'object') {
      selectedField.default = JSON.stringify(selectedField.default)
    }
    schemaData.type.defaultVal = selectedField.default?.toString() ?? '';
    schemaData.type.unit = selectedField.unit ?? "";



    // setting values for hyperlink attributes
    if (parentData.type === 'table' && selectedField.type !== "table-button" && !urlTypes.includes(selectedField.type)) {
      schemaData.type.hyperlink = { "disable": "disable" }

      if (selectedField.viewname !== undefined) {

        let urlType = {
          "urlView": "urlView",
          "viewname": selectedField.viewname,
          "instance": selectedField.instance,
          "queryParameters": (selectedField.queryparams && selectedField.queryparams?.length > 0) ? selectedField.queryparams : [{Key: "", Value: ""}]
        }
        
        schemaData.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }
      }

      if (selectedField.clusterlettype !== undefined) {
        const urlType = {
          "urlClusterlet": "urlClusterlet",
          "clusterlettype": selectedField.clusterlettype,
          "instance": selectedField.instance,
          "queryParameters": (selectedField.queryparams && selectedField.queryparams?.length > 0) ? selectedField.queryparams : [{Key: "", Value: ""}]
        }
        schemaData.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }
      }


      if (selectedField.url !== undefined) {
        const urlType = {
          "urlExternal": "urlExternal",
          "url": selectedField.url
        }
        schemaData.type.hyperlink = {
          "enable": "enable",
          "urlType": urlType
        }
      }

      if (schemaData?.children?.length > 0) {
        delete schemaData.type.hyperlink
      }
    }

    // 'visibility' property for view copy item 
    if (selectedField?.viewCopy != undefined && selectedField?.viewCopy === true) {
      schemaData.visible = selectedField.visible ?? true;
    }

    return schemaData;
  } else {
    return {
      name: '',
      desc: '',
      type: {
        "select": "select",
        "unit": ""
      }
    }
  }
}



export const fieldFormValidation = ({ schema, value, path, fields, selectedField }) => {
  let errors = [];
  if (path === "field_form.name" && value === "") {
    errors.push({
      path,
      property: 'format',
      message: "Name is required"
    });
  }

  if (path === "field_form.name" && isValidSimpleIdentifier(value) === false) {
    errors.push({
      path,
      property: 'format',
      message: "Name can only contain letters, numbers, and underscores"
    });
  }

  if (path === "field_form.desc" && validateNonAsciiChars(value) !== true) {
    errors.push({
      path,
      property: 'format',
      message: validateNonAsciiChars(value)
    })
  }

  // const queryParameters = value?.type?.hyperlink?.urlType?.queryParameters;
  // console.log({queryParameters})
  // if(queryParameters !== undefined && queryParameters?.length > 0){
  //   validateQuerayParametersInHyperlink(queryParameters, errors, 'field_form.type.hyperlink.urlType.queryParameters')
  // }

  return errors;
}




// ########################### Data Source mapping #####################################



const generateInBuiltTransaction = (clusterlets) => {

  return clusterlets.map(obj => {
    let newObj = {
      ...obj,
      name: `get_${obj.name}`
    }
    return newObj;
  })

}


export const mappingTransactionSchema = function (transactions, isFieldRequired, clusterlets, events = [], delegates = [], isResponseEventsRequired = false, isClusterletRequired = false, fieldName = "", isView = false) {
  let inbuildTransactions = [];
  if (isFieldRequired && clusterlets != undefined) {
    let filteredClusterlets = clusterlets.filter(item => item.type == 2 || item.type == 3)
    inbuildTransactions = generateInBuiltTransaction(filteredClusterlets) || []
  }
  let inbuildTransactionsIds = inbuildTransactions?.map(item => item._id);
  let filteredTransaction = transactions.filter(txn => txn.type == 7)
  const combinedTransactions = isView ? filteredTransaction : [...filteredTransaction, ...inbuildTransactions];
  const filteredCombinedTransactions = combinedTransactions?.sort((a, b) => a.name.localeCompare(b.name)) || [];
  let transactionMappingSchema = [{ name: "Select", _id: 'select' }, ...filteredCombinedTransactions].map((item) => {


    let singleSchema = {
      title: item.name,
      type: "object",
      default: item._id,
      format: "grid",
      properties: {
        [item._id]: { default: item._id, type: 'string', options: { hidden: true } },
      },
      options: {
        layout: 'grid',
        grid_columns: 3
      },
      additionalProperties: false
    };

    let schema = {}

    // schema for datasource mapping
    if (item._id != "select" && isClusterletRequired) {
      // in case of user defined transactions
      if (!inbuildTransactionsIds.includes(item._id)) {
        // Triggering event
        schema.event = {
          title: 'Event name',
          type: 'string',
          readOnly: true,
          options: {
            layout: 'grid',
            grid_columns: 6
          }
        }

        schema.field = {
          title: 'Field',
          type: 'string',
          icon: "fa-check-circle",
          options: {
            icon: "fa-check-circle",
            layout: 'grid',
            grid_columns: 4
          }
        }
      }

      schema.clusterlet = {
        title: 'Target clusterlet',
        type: 'string',
        readOnly: true,
        options: {
          layout: 'grid',
          grid_columns: 6
        }
      }
      schema.instance = {
        title: 'Instance',
        default: '{instance}',
        type: 'string',
        options: {
          layout: 'grid',
          grid_columns: 4
        }
      }

      if (inbuildTransactionsIds.includes(item._id)) {
        schema.field = {
          title: 'Field',
          type: 'string',
          icon: "fa-check-circle",
          options: {
            icon: "fa-check-circle",
            layout: 'grid',
            grid_columns: 12
          }
        }
      }

    }

    if (item._id != "select" && !isFieldRequired) {
      schema.instance = {
        title: 'Instance',
        default: '{instance}',
        type: 'string',
        options: {
          layout: 'grid',
          grid_columns: 4
        }
      }
    }

    singleSchema = { ...singleSchema, properties: { ...singleSchema.properties, ...schema } };
    return singleSchema;
  });


  return {
    transaction: {
      title: fieldName == "" ? 'Transaction' : fieldName,
      oneOf: [...transactionMappingSchema],
      options: {
        disable_properties: true,
        layout: 'grid',
        grid_columns: 12
      }
    }
  }

}


export const getMappingSchema = ({ transactions, clusterlets, delegates, events, isView }) => {
  let transactionMappingSchema = mappingTransactionSchema(transactions, true, [...clusterlets, ...delegates], events, delegates, true, true, "", isView);
  let schema = {
    type: 'object',
    required: [],
    options: {
      layout: 'grid',
      disable_properties: 'false'
    },
    properties: {
      transaction: transactionMappingSchema.transaction
    }
  }
  return schema;
}


export const getMappingSchemaData = ({ selectedEntity, transactions, events, clusterlets }) => {
  let schemaValues;
  if (selectedEntity !== undefined && selectedEntity !== null) {
    schemaValues = { ...selectedEntity };
    if (selectedEntity.mapping != null) {
      schemaValues = {
        transaction: selectedEntity.mapping.source !== undefined ? {} : { 'select': 'select' }
      }

      if (selectedEntity.mapping.transaction != null && selectedEntity.mapping.source === 'transaction') {

        // finding clusterlet type
        const transaction = transactions.find(item => item._id == selectedEntity.mapping.transaction);
        let clusterletType = "";
        let triggerEvent = null;
        if (transaction) {
          triggerEvent = findTriggerEvent({ transaction, clusterlets, events });
          if (triggerEvent) {
            clusterletType = clusterlets.find(item => item._id == triggerEvent.dst)
          }
        } else {
          clusterletType = clusterlets.find(item => item._id == selectedEntity.mapping.transaction);
        }

        schemaValues.transaction = selectedEntity.mapping.transaction !== undefined ? { [selectedEntity.mapping.transaction]: selectedEntity.mapping.transaction } : { 'select': 'select' };
        schemaValues.transaction.instance = selectedEntity.mapping.instance !== undefined ? selectedEntity.mapping.instance : '';
        schemaValues.transaction.field = selectedEntity.mapping.field !== undefined ? selectedEntity.mapping.field : '';
        schemaValues.transaction.clusterlet = clusterletType != undefined ? clusterletType.name : '';
        if (triggerEvent != null) schemaValues.transaction.event = triggerEvent.name;

      } else{
        schemaValues.transaction = { 'select': 'select' };
      }

    } else {
      schemaValues.transaction = { 'select': 'select' };
    }
  }

  return schemaValues;
}



export const customizeMappingForm = ({ data, randomNumber, clusterlets, events, delegates, transactions, setCreateTransaction, type, generateWidgetTransaction }) => {

  const clusteletTypeFields = document.querySelectorAll('input[id="datasource[transaction][clusterlet]"]');
  const eventInputFields = document.querySelectorAll('input[id="datasource[transaction][event]"]');
  if (data.transaction !== undefined && clusteletTypeFields.length > 0) {
    const transactionId = Object.keys(data.transaction)[0];
    const transaction = transactions.find(item => item._id == transactionId);
    let triggerEvent = null;

    let clusterletType;
    if (transaction !== undefined) {
      triggerEvent = findTriggerEvent({ transaction: transaction, events: events, clusterlets: [...clusterlets, ...delegates] })
      clusterletType = triggerEvent ? clusterlets.find(clst => clst._id == triggerEvent.dst) : null;
    } else {
      clusterletType = clusterlets.find(clst => clst._id == transactionId);
    }
    
    // setting value to the target clusterlet input field
    clusteletTypeFields.forEach((inputField, index) => {
      let element = clusteletTypeFields[index]
      if (element !== undefined) {
        element.value = clusterletType ? clusterletType.name : data.transaction.clusterlet ? data.transaction.clusterlet : "";
        data.transaction.clusterlet = clusterletType ? clusterletType.name : data.transaction.clusterlet ? data.transaction.clusterlet : "";
      }
    });

    // setting value to the event input field
    if (eventInputFields.length > 0 && triggerEvent != null && triggerEvent != undefined) {
      eventInputFields.forEach((inputField, index) => {
        let element = eventInputFields[index]
        if (element !== undefined) {
          element.value = triggerEvent.name;
          data.transaction.event = triggerEvent.name;
        }
      });
    }

  }

  // Event configuration edit button 
  let fieldDiv2 = document.querySelectorAll(`[data-schemapath="datasource.transaction.field"]`);
  if (data?.transaction && fieldDiv2 !== null && fieldDiv2 !== undefined && fieldDiv2[0] !== null && fieldDiv2[0] !== undefined) {

    // Create the button element
    const buttonElement = document.createElement('button');
    buttonElement.id = `field-tree-event-btn${randomNumber}`;
    buttonElement.title = 'Click to select the field from message configuration';
    buttonElement.style.cursor = 'pointer';
    buttonElement.style.border = '1px solid #ced4da';
    buttonElement.style.borderRadius = '0rem 0.25rem 0.25rem 0rem';

    // Create the icon element for the button
    const iconElement = document.createElement('i');
    iconElement.className = 'fa fa-edit';
    iconElement.style.padding = '10px 16px';

    // Append the icon to the button
    buttonElement.appendChild(iconElement);

    const parentDiv = document.createElement('div');
    parentDiv.id = `event-field-container-${randomNumber}`;
    parentDiv.style.display = 'flex';
    parentDiv.style.alignItems = 'flex-start';
    // Find the 'form-group' div inside fieldDiv and append the button to it
    const formGroupDiv = fieldDiv2[fieldDiv2.length - 1].querySelector('.form-group');   // Each event has different fieldDiv2 element
    if (formGroupDiv !== null && formGroupDiv !== undefined) {
      const labelElement = formGroupDiv.querySelector('label');
      const inputElement = formGroupDiv.querySelector('input');
      if (labelElement !== undefined && inputElement !== undefined && inputElement != null) {
        formGroupDiv.innerHTML = '<label for="datasource[transaction][field]">Field</label>';
        inputElement.style.borderRadius = '0.25rem 0rem 0rem 0.25rem';
        parentDiv.appendChild(inputElement)
        parentDiv.appendChild(buttonElement);
        formGroupDiv.appendChild(parentDiv);
      }
    }
  }
// ---------- Create "or Create Transaction" label just below dropdown ----------
// ---------- Create "or Create Transaction" label just below dropdown ----------
const transactionContainer = document.querySelector('[data-schemapath="datasource.transaction"]');
  
if (transactionContainer) {
  const selectDropdown = transactionContainer.querySelector('select');
  
  if (selectDropdown) {
    const existingLabel = transactionContainer.querySelector('.custom-create-tx-link');
    if (existingLabel) existingLabel.remove();
    const labelContainer = document.createElement('div');
    labelContainer.className = 'custom-create-tx-link'; 
    
    // FIXING CLICKABILITY: Added position and zIndex
    labelContainer.style.position = 'relative'; // Allows z-index to work
    labelContainer.style.zIndex = '10';         // Places it above overlapping fields
    labelContainer.style.margin = '2px 0px -12px 2px'; 
    labelContainer.style.fontSize = '14px';
    labelContainer.style.fontFamily = 'inherit';
    labelContainer.style.lineHeight = '1';

    

    if(type === "ui"){
      labelContainer.innerHTML = `
        <span style="
          display: none;
          width: 14px;
          height: 14px;
          margin-right: 8px;
          vertical-align: middle;
          background-image: url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Ccircle cx=%2250%22 cy=%2250%22 r=%2240%22 stroke=%22%23007bff%22 stroke-width=%2210%22 fill=%22none%22 stroke-dasharray=%22150 100%22%3E%3CanimateTransform attributeName=%22transform%22 type=%22rotate%22 from=%220 50 50%22 to=%22360 50 50%22 dur=%221s%22 repeatCount=%22indefinite%22/%3E%3C/circle%3E%3C/svg%3E');
          background-size: contain;
          background-repeat: no-repeat;
        "
        id="trigger-txn-create-loader"
        ></span>
        <span id="trigger-btns-wrapper">
          <span style="
              color: #000; 
              font-weight: 400;
              font-size : 14px;
              text-decoration: none;
              position: relative; 
              z-index: 11;
          " >Need help?</span>
          <span id="trigger-create-with-ai-tx-action" style="
              color: #007bff; 
              cursor: pointer; 
              font-weight: 400;
              font-size : 14px;
              text-decoration: none;
              position: relative; 
              z-index: 11;
          ">Generate a transaction with AI</span>
          <span style="
              color: #000; 
              font-weight: 400;
              font-size : 14px;
              text-decoration: none;
              position: relative; 
              z-index: 11;
          " >or</span>
          <span id="trigger-create-tx-action" style="
              color: #007bff; 
              cursor: pointer; 
              font-weight: 400;
              font-size : 14px;
              text-decoration: none;
              position: relative; 
              z-index: 11;
          ">Create Manually</span>
        </span>
    `
    }else{
      labelContainer.innerHTML = `
      <span id="trigger-create-tx-action" style="
          color: #007bff; 
          cursor: pointer; 
          font-weight: 400;
          font-size : 14px;
          text-decoration: none;
          position: relative; 
          z-index: 11;
      ">Or Create a Transaction</span>
    `;
    }

    selectDropdown.parentNode.insertBefore(labelContainer, selectDropdown.nextSibling);

    // Ensure the lower container doesn't have a higher z-index
    const fieldsContainer = labelContainer.nextElementSibling;
    if (fieldsContainer) {
        fieldsContainer.style.marginTop = '0px';
        fieldsContainer.style.paddingTop = '0px';
        fieldsContainer.style.position = 'relative';
        fieldsContainer.style.zIndex = '1'; // Keep fields below the label
    }

    const linkBtn = document.getElementById('trigger-create-tx-action');
    const generateBtn = document.getElementById('trigger-create-with-ai-tx-action');
    if (linkBtn) {
      linkBtn.onmouseover = () => { linkBtn.style.textDecoration = 'underline'; };
      linkBtn.onmouseout = () => { linkBtn.style.textDecoration = 'none'; };

      linkBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Stop event from bubbling to the dropdown
        if (typeof setCreateTransaction === 'function') {
          setCreateTransaction(true);
        }
      };
    }
    if (generateBtn) {
      generateBtn.onmouseover = () => { generateBtn.style.textDecoration = 'underline'; };
      generateBtn.onmouseout = () => { generateBtn.style.textDecoration = 'none'; };

      generateBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Stop event from bubbling to the dropdown
        if (typeof generateWidgetTransaction === 'function') {
          generateWidgetTransaction();
        }
      };
    }
  }
}
}